import { examTestSchema, type ExamTest } from '@fluent/types';

/**
 * The 120 mock tests. Each test is ~15 KB of JSON, so unlike lessons they
 * are NOT bundled eagerly: the glob is lazy and a test is fetched (and
 * validated) only when it is opened. The list screen needs just numbers
 * and levels, which come from the file names and EXAM.md's ramp.
 */

const testModules = import.meta.glob<unknown>('../../../../content/exam/test-*.json', {
  import: 'default',
});

export const EXAM_TEST_COUNT = 120;

/** Tests anyone can take without Pro (Tornike, 2026-09-26): a taste of the
 *  simulator, and a way for free learners to do the daily and weekly
 *  test-task missions. The server enforced the same rule (complete_exam_task
 *  in migration 0021) until everything went free (freeMode.ts, 0027). */
export const FREE_EXAM_TESTS = 3;

export function isFreeExamTest(n: number | null): boolean {
  return n !== null && n >= 1 && n <= FREE_EXAM_TESTS;
}

export type ExamLevel = 1 | 2 | 3 | 4 | 5;

/** EXAM.md § Difficulty: 1–20 · 21–50 · 51–80 · 81–100 (the real exam's
 *  level) · 101–120 (harder than the exam — Tornike 2026-09-26). */
export const EXAM_LEVELS: readonly { level: ExamLevel; from: number; to: number }[] = [
  { level: 1, from: 1, to: 20 },
  { level: 2, from: 21, to: 50 },
  { level: 3, from: 51, to: 80 },
  { level: 4, from: 81, to: 100 },
  { level: 5, from: 101, to: 120 },
];

export function levelOf(n: number): ExamLevel {
  return EXAM_LEVELS.find((l) => n >= l.from && n <= l.to)?.level ?? 5;
}

export function testIdOf(n: number): string {
  return `test-${String(n).padStart(3, '0')}`;
}

export function numberOf(testId: string): number | null {
  const m = /^test-(\d{3})$/u.exec(testId);
  return m ? Number(m[1]) : null;
}

const pathById = new Map<string, string>();
for (const path of Object.keys(testModules)) {
  const m = /(test-\d{3})\.json$/u.exec(path);
  if (m?.[1]) pathById.set(m[1], path);
}

/** Whether a test has been written yet (the catalog fills up over time). */
export function isTestAvailable(testId: string): boolean {
  return pathById.has(testId);
}

export async function loadExamTest(testId: string): Promise<ExamTest | null> {
  const path = pathById.get(testId);
  const load = path ? testModules[path] : undefined;
  if (!load) return null;
  const parsed = examTestSchema.safeParse(await load());
  if (!parsed.success) {
    throw new Error(`Invalid exam test ${testId}: ${parsed.error.message}`);
  }
  return parsed.data;
}
